import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, CreditCard, Loader2, Trash2 } from 'lucide-react';
import { PhotoPicker } from '../driver/PhotoPicker';
import { photoSrc } from '../../lib/photos';
import { BrandLogo } from '../BrandLogo';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [age, setAge] = useState(user?.age ?? '');
  const [gender, setGender] = useState(user?.gender || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardsNote, setCardsNote] = useState('');
  const [loadingCards, setLoadingCards] = useState(true);

  const loadCards = async () => {
    setLoadingCards(true);
    try {
      const { data } = await api.get('/api/users/payment-methods');
      setCards(data.cards || []);
      setCardsNote(data.note || '');
    } catch {
      setCardsNote('Could not load saved cards right now.');
    } finally {
      setLoadingCards(false);
    }
  };

  useEffect(() => { loadCards(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/api/auth/user/profile', {
        name: name || undefined,
        age: age === '' ? undefined : Number(age),
        gender: gender || undefined,
        email: email || undefined,
      });
      updateUser({
        name: data.name, age: data.age, gender: data.gender, email: data.email,
      });
      toast({ title: 'Profile updated', description: 'Your details have been saved' });
    } catch (err) {
      toast({ title: 'Could not save profile', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCard = async (tokenId) => {
    try {
      const { data } = await api.delete(`/api/users/payment-methods/${tokenId}`);
      setCards(data.cards || []);
      toast({ title: 'Card removed' });
    } catch (err) {
      toast({ title: 'Could not remove card', description: formatApiError(err), variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/user/dashboard')} data-testid="profile-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <BrandLogo variant="compact" className="h-8 w-auto" />
          <h1 className="text-xl font-bold border-l pl-4">My Profile</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-6">
        <Card className="p-6">
          <PhotoPicker
            currentSrc={photoSrc(user?.photoUrl)}
            initials={user?.name?.[0] || 'U'}
            size="w-20 h-20"
            testId="user-photo"
            upload={async (file) => {
              const form = new FormData();
              form.append('file', file);
              const { data } = await api.post('/api/auth/user/photo', form);
              return data;
            }}
            onUploaded={(data) => {
              updateUser({ photoUrl: data.photo_url });
              toast({ title: 'Photo updated' });
            }}
          />
        </Card>

        <Card className="p-6 space-y-4">
          <h2 className="font-semibold text-lg">Personal Details</h2>
          <div>
            <Label htmlFor="profile-name">Full Name</Label>
            <Input id="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11"
              data-testid="profile-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="profile-age">Age</Label>
              <Input id="profile-age" type="number" min="1" max="120" value={age}
                onChange={(e) => setAge(e.target.value)} className="h-11" data-testid="profile-age-input" />
            </div>
            <div>
              <Label htmlFor="profile-email">Email</Label>
              <Input id="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11" data-testid="profile-email-input" />
            </div>
          </div>
          <div>
            <Label>Gender</Label>
            <div className="grid grid-cols-3 gap-2 mt-1" data-testid="profile-gender-group">
              {['male', 'female', 'other'].map((g) => (
                <button key={g} type="button" onClick={() => setGender(g)} data-testid={`profile-gender-${g}`}
                  className={`h-11 rounded-md border-2 text-sm font-medium capitalize transition-colors ${
                    gender === g ? 'border-primary bg-muted' : 'border-border hover:border-border'
                  }`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
          <Button className="w-full bg-primary hover:bg-primary-dark" onClick={handleSave} disabled={saving}
            data-testid="profile-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save Changes'}
          </Button>
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />Payment Modes
          </h2>
          {loadingCards ? (
            <p className="text-sm text-muted-foreground">Loading saved cards…</p>
          ) : cards.length > 0 ? (
            <div className="space-y-3">
              {cards.map((c) => (
                <div key={c.token_id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`saved-card-${c.token_id}`}>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium capitalize">{c.network || 'Card'} •••• {c.last4 || '----'}</p>
                      {c.expiry_month && <p className="text-xs text-muted-foreground">Expires {c.expiry_month}/{c.expiry_year}</p>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteCard(c.token_id)}
                    data-testid={`delete-card-${c.token_id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="no-saved-cards-note">
              {cardsNote || 'No saved cards yet. Choose "save card" during your next payment to add one here.'}
            </p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ProfilePage;
